import "./bazi-table-style.css";
import { React, useState, useEffect } from "react";
import BaziExpert from "../bazi-expert/bazi-expert-dialog";

//八字命盤用 HTML <table> + colspan 排版 — 避開 iOS Safari grid 1fr 不均分 bug

const GAN_ELEMENT = {
  甲: "木", 乙: "木", 丙: "火", 丁: "火", 戊: "土",
  己: "土", 庚: "金", 辛: "金", 壬: "水", 癸: "水",
};

const ZHI_ELEMENT = {
  子: "水", 丑: "土", 寅: "木", 卯: "木", 辰: "土",
  巳: "火", 午: "火", 未: "土", 申: "金", 酉: "金", 戌: "土", 亥: "水",
};

const pad3 = (arr) => {
  const result = [...(arr || [])];
  while (result.length < 3) result.push("");
  return result.reverse();
};

//柱位中文名（配合傳統排法：時柱在左、年柱在右；跟 buildPillars 陣列順序一致）
const PILLAR_LABEL = {
  time: "時柱",
  day: "日柱",
  month: "月柱",
  year: "年柱",
};

const buildPillars = (bp) => [
  { key: "time",  title: bp.timeColumn.shiShenGan,
    gan:  { char: bp.timeColumn.gan, element: GAN_ELEMENT[bp.timeColumn.gan] },
    zhi:  { char: bp.timeColumn.zhi, element: ZHI_ELEMENT[bp.timeColumn.zhi] },
    hiddenGans: pad3(bp.timeColumn.hideGan),
    hiddenGods: pad3(bp.timeColumn.shiShenZhi),
  },
  { key: "day",  title: "日主",
    gan:  { char: bp.dayColumn.gan, element: GAN_ELEMENT[bp.dayColumn.gan] },
    zhi:  { char: bp.dayColumn.zhi, element: ZHI_ELEMENT[bp.dayColumn.zhi] },
    hiddenGans: pad3(bp.dayColumn.hideGan),
    hiddenGods: pad3(bp.dayColumn.shiShenZhi),
  },
  { key: "month",  title: bp.monthColumn.shiShenGan,
    gan:  { char: bp.monthColumn.gan, element: GAN_ELEMENT[bp.monthColumn.gan] },
    zhi:  { char: bp.monthColumn.zhi, element: ZHI_ELEMENT[bp.monthColumn.zhi] },
    hiddenGans: pad3(bp.monthColumn.hideGan),
    hiddenGods: pad3(bp.monthColumn.shiShenZhi),
  },
  { key: "year",  title: bp.yearColumn.shiShenGan,
    gan:  { char: bp.yearColumn.gan, element: GAN_ELEMENT[bp.yearColumn.gan] },
    zhi:  { char: bp.yearColumn.zhi, element: ZHI_ELEMENT[bp.yearColumn.zhi] },
    hiddenGans: pad3(bp.yearColumn.hideGan),
    hiddenGods: pad3(bp.yearColumn.shiShenZhi),
  },
];

const buildSummary = (bp) => ({
  hexagram: { name: bp.hexagram.hexagramName, direction: bp.hexagram.direction },
  physique: bp.physique,
  kuma: { ma: bp.kuMaHua.ma, hua: bp.kuMaHua.fl, ku: bp.kuMaHua.ku },
  elements: bp.elements,
});

const buildDecades = (daYunList) =>
  daYunList.map((d) => ({
    start: d.year,
    age: d.startAge,
    fortune: [
      { sign: d.ganZhi?.[0] ?? "", tenGod: d.shenZhi?.[0] ?? "" },
      { sign: d.ganZhi?.[1] ?? "", tenGod: d.shenZhi?.[1] ?? "" },
    ],
    yearList: d.yearList ?? [],
  }));

const EMPTY_PILLAR = {
  title: "",
  gan: { char: "", element: "" },
  zhi: { char: "", element: "" },
  hiddenGans: ["", "", ""],
  hiddenGods: ["", "", ""],
};
const EMPTY_PILLARS = ["time", "day", "month", "year"].map((key) => ({
  key,
  ...EMPTY_PILLAR,
}));

const BaziBoard = ({ baziPlate }) => {
  const pillars = baziPlate ? buildPillars(baziPlate) : EMPTY_PILLARS;
  const summary = baziPlate ? buildSummary(baziPlate) : null;
  const DECADES = baziPlate ? buildDecades(baziPlate.daYunList) : [];

  const currentYear = new Date().getFullYear();
  const currentDecade =
    DECADES.find((d) => currentYear >= d.start && currentYear < d.start + 10) ??
    DECADES[0];

  const [activeDecadeStart, setActiveDecadeStart] = useState(null);

  useEffect(() => {
    if (DECADES.length > 0 && !activeDecadeStart) {
      setActiveDecadeStart(currentDecade?.start ?? DECADES[0].start);
    }
  }, [DECADES.length]);

  const years = DECADES.find((d) => d.start === activeDecadeStart)?.yearList ?? [];

  const [showBaziExpert, setShowBaziExpert] = useState(false);

  return (
    <div className="bazi">
      <div className="table-title">{baziPlate?.baziBirth}</div>

      {/* 四柱命盤 — HTML <table> 版 */}
      <table className="bazi-main">
        <colgroup>
          {/* 4 個主欄各佔 1fr（瀏覽器 table layout 自動均分），最後欄給 row label
              clamp 讓 iPhone 11 Pro (375px) 等窄螢幕縮到 28px，避免擠爆右側 */}
          <col span="12" />
          <col style={{ width: "clamp(28px, 7%, 35px)" }} />
        </colgroup>
        <tbody>
          {/* 柱位 header：時柱 / 日柱 / 月柱 / 年柱（由左至右） */}
          <tr className="row-pillar-head">
            {pillars.map((p) => (
              <td key={p.key} colSpan={3} className="pillar-head">
                {PILLAR_LABEL[p.key]}
              </td>
            ))}
            <td className="pillar-head"></td>
          </tr>

          {/* 十神列 */}
          <tr className="row-shishen">
            {pillars.map((p) => (
              <td key={p.key} colSpan={3} className="cell shishen">
                {p.title}
              </td>
            ))}
            <td className="cell-head">十神</td>
          </tr>

          {/* 天干列 */}
          <tr className="row-tiangan">
            {pillars.map((p) => (
              <td key={p.key} colSpan={3} className="cell larget-cell">
                <span className="char">{p.gan.char}</span>
                <span className="element">{p.gan.element}</span>
              </td>
            ))}
            <td className="cell-head">天干</td>
          </tr>

          {/* 地支列 */}
          <tr className="row-dizhi">
            {pillars.map((p) => (
              <td key={p.key} colSpan={3} className="cell larget-cell">
                <span className="char">{p.zhi.char}</span>
                <span className="element">{p.zhi.element}</span>
              </td>
            ))}
            <td className="cell-head">地支</td>
          </tr>

          {/* 藏干列 */}
          <tr className="row-inner">
            {pillars.flatMap((p) =>
              p.hiddenGans.map((hid, i) => (
                <td key={`${p.key}-hg-${i}`} className="cell inner">
                  {hid}
                </td>
              ))
            )}
            <td className="cell-head">藏干</td>
          </tr>

          {/* 藏干十神列 */}
          <tr className="row-inner">
            {pillars.flatMap((p) =>
              p.hiddenGods.map((hid, i) => (
                <td key={`${p.key}-hgd-${i}`} className="cell inner">
                  {hid}
                </td>
              ))
            )}
            <td className="cell-head">十神</td>
          </tr>
        </tbody>
      </table>

      {summary && (
        <div className="summary">
          <div className="summary-row">
            <div>
              命卦：{summary.hexagram.name}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {summary.hexagram.direction}
            </div>
            <div>{summary.physique}</div>
          </div>
          <div className="summary-row">
            <div>{`馬：${summary.kuma.ma}`}</div>
            <div>{`花：${summary.kuma.hua}`}</div>
            <div>{`庫：${summary.kuma.ku}`}</div>
          </div>
          <div className="summary-row">
            <div>{`木：${summary.elements.wood}`}</div>
            <div>{`火：${summary.elements.fire}`}</div>
            <div>{`土：${summary.elements.earth}`}</div>
            <div>{`金：${summary.elements.metal}`}</div>
            <div>{`水：${summary.elements.water}`}</div>
          </div>
        </div>
      )}

      <div className="expert">
        <img
          onClick={() => setShowBaziExpert(true)}
          src="../images/ic-description.png"
          alt="專業分析"
        />
      </div>

      <div className="table-title">十年大運／流年運勢</div>

      <div className="luck-wrap">
        {/* 十年大運 — 每資料一個 <tr>，這樣每個 td 才有完整 border 形成 grid
            （原本「每個 decade 1 td 內塞多個 div」會少橫向格線） */}
        <table className="luck-table decade-table">
          <tbody>
            <tr>
              {DECADES.map((d) => {
                const isActive = d.start === activeDecadeStart;
                return (
                  <td
                    key={d.start}
                    className={`select ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveDecadeStart(d.start)}
                  >
                    {d.start}
                  </td>
                );
              })}
            </tr>
            <tr>
              {DECADES.map((d) => {
                const isActive = d.start === activeDecadeStart;
                return (
                  <td
                    key={d.start}
                    className={`select ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveDecadeStart(d.start)}
                  >
                    {d.age}歲
                  </td>
                );
              })}
            </tr>
            {/* 天干、地支兩個 fortune 合併在同一 row 的同一 td，中間不要橫線 */}
            <tr>
              {DECADES.map((d) => {
                const isActive = d.start === activeDecadeStart;
                return (
                  <td
                    key={d.start}
                    className={`select inner-cell ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveDecadeStart(d.start)}
                  >
                    {d.fortune.map((f, i) => (
                      <div key={i} className="fortune-line">
                        <span className="sign">{f.sign || " "}</span>
                        <span className="ten-god">{f.tenGod || " "}</span>
                      </div>
                    ))}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        {/* 流年 — 同樣每資料一個 <tr> */}
        <table className="luck-table year-table">
          <tbody>
            <tr>
              {years.map((y) => (
                <td
                  key={y.year}
                  className={`${currentYear == y.year ? "is-active" : ""}`}
                >
                  {y.yearTenGod}
                </td>
              ))}
            </tr>
            <tr>
              {years.map((y) => (
                <td
                  key={y.year}
                  className={`${currentYear == y.year ? "is-active" : ""}`}
                >
                  {y.year}
                </td>
              ))}
            </tr>
            <tr>
              {years.map((y) => (
                <td
                  key={y.year}
                  className={`year-sign ${currentYear == y.year ? "is-active" : ""}`}
                >
                  <span>{y.yearGan}</span>
                  <span>{y.yearZhi}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {baziPlate?.liChunInfo && (
        <div className="warning">
          *{baziPlate.liChunInfo.ganZhi}從{baziPlate.liChunInfo.date}開始
        </div>
      )}

      {showBaziExpert && (
        <BaziExpert
          open={showBaziExpert}
          onClose={() => setShowBaziExpert(false)}
          title="專業分析"
          baziPlate={baziPlate}
        />
      )}
    </div>
  );
};

export default BaziBoard;
